/* global lt */

import { getIdByGraphQlId } from 'libs/shopify'
import normalizeProducts from 'libs/shopify-normalize'
import { fetchProductByIds } from 'libs/shopify-product'
import { forceSetRecentlyProduct, setRecentlyProducts } from 'helpers/recently-product'

/**
 * Returns a curried function of the provided function, so that:
 *
 * f(a, b, c) = f(a, b)(c) = f(a)(b)(c) = f(a)(b, c)
 *
 * @param {Function} f
 * @param {..*} Initial parameters
 * @return {Function} The curried function
 */
const curry = (f, ...args) => (args.length >= f.length ? f(...args) : curry.bind(this, f, ...args))

/**
 * Functional wrapper for array map function.
 *
 * @param {Function} f
 * @param {*} arr
 */
const map = curry((f, arr) => (Array.isArray(arr) ? arr.map(f) : f(arr)))

/**
 * Partial application
 * @param {Function} f
 * @param {..*} args Initial parameters
 */
const partial = (f, ...args) => f.bind(this, ...args)

/**
 * Transform a value by chaining a list of function from left to right
 *
 * @param val
 * @param {..Function} funcs
 * @return {*}
 */
const pipe = (...funcs) =>
  // eslint-disable-next-line func-names
  function (val) {
    return funcs.reduce((acc, f) => f.apply(this, [acc]), val)
  }

const getProp = curry((prop, obj) => obj[prop])

const setProp = curry((prop, value, obj) => {
  // eslint-disable-next-line no-param-reassign
  obj[prop] = value

  return obj
})

/**
 * Delay execution of callback used on individual items of a list.
 *
 * This function is curried.
 *
 * @param {number} initial Initial timeout
 * @param {number} step Delay per execution
 * @param {Function} callback Callback
 * @param {Array} items
 */

const whileDo = (pred, fn, initial) => (pred(initial) ? whileDo(pred, fn, fn(initial)) : initial)

/**
 * Check if an array contains an item.
 *
 * This is a curried wrapper for Array.prototype.indexOf
 *
 * @param {Array}
 * @param {*}
 * @return {boolean}
 */
const inArray = curry((array, item) => array.indexOf(item) !== -1)

/**
 * Get the nth element of an array
 *
 * @param {number}
 * @param {Array}
 * @return {*}
 */
const nth = curry((index, array) => (index < 0 ? array[array.length + index] : array[index]))

/**
 * Get the nth element of an array
 *
 * @param {Array}
 * @return {*}
 */
const first = nth(1)
const last = nth(-1)
const add = curry((b, a) => a + b)
const _add1ToLast = pipe(last, add(1))

const range = (from, to) => whileDo(pipe(_add1ToLast, lt(to)), (array) => [...array, _add1ToLast(array)], [from])

/**
 * Create an array out of an array-like object
 *
 * @param {Object} Array-like object
 * @return {Array} Array
 */
const makeArray = (arrayLike) => Array.prototype.slice.call(arrayLike)

const set = (item, selector) => {
  if (item instanceof Array) {
    item.forEach((i) => i.classList.add(selector))
  } else {
    item.classList.add(selector)
  }
}

const unset = (item, selector) => {
  if (item instanceof Array) {
    item.forEach((i) => i.classList.remove(selector))
  } else {
    item.classList.remove(selector)
  }
}

const hasOwnProperties = (property, obj) => Object.prototype.hasOwnProperty.call(obj, property)

const inViewPort = (el, callback) => {
  const rect = el.getBoundingClientRect()

  rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth) &&
    typeof callback === 'function' &&
    callback()
}

const handleize = (string) =>
  string
    .toLowerCase()
    .replace(/[^\w\u00C0-\u024f]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const isEmpty = (val) => val == null || !(Object.keys(val) || val).length

const formatName = (string) =>
  string && typeof string === 'string' && string.includes('-') ? string.split('-')[1] : string

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

/**
 * Uses the DOMParser API to parse the encoded string as an HTML document.
 * Accesses the textContent of the <body> to retrieve the decoded text.
 *
 * Avoids creating additional DOM elements.
 * More efficient for frequent calls as it leverages native browser APIs designed for parsing HTML.
 *
 * @param {string} string
 * @return
 */
const decodeHtml = (string) => new DOMParser().parseFromString(string, 'text/html').body.textContent

const decode = (str) => decodeURIComponent(str).replace(/\+/g, ' ')

const formatDate = (dateString) => {
  const date = new Date(dateString)

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date)
}

const mapWithOptions = curry((fn, options, arr) =>
  Array.isArray(arr) ? arr.map((el) => fn(el, options)) : fn(arr, options)
)

function numberToWords(number) {
  const singleDigits = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']

  const tenToFifteen = ['ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen']

  if (number < 1 || number > 15) return ''
  if (number < 10) return singleDigits[number]

  return tenToFifteen[number - 10]
}

const fetchProductsRecentlyViewed = async (idsRecentlyViewed, currentProductId, limit = 10) => {
  if (!idsRecentlyViewed.length) return []

  const allProducts = await fetchProductByIds(idsRecentlyViewed)

  const filteredProducts = allProducts.filter((item) => item)

  if (!filteredProducts.length) {
    return []
  }

  const failedProducts = allProducts.some((item) => !item)

  if (failedProducts && currentProductId) {
    forceSetRecentlyProduct(filteredProducts.map((item) => getIdByGraphQlId(item.id)).join('|'))
    setRecentlyProducts(currentProductId, Number.parseInt(limit, 10))
  }

  return normalizeProducts(filteredProducts, 'shopify-graphql')
}

const getOutOfStockMessage = (initProduct) => {
  if (initProduct.available) return null

  const { soldOutMessage, temporaryOutOfStockMessage } = GM_STATE.product
  const hasNoNotifyMeTag = initProduct.tags && initProduct.tags.includes('no-notify-me')

  if (hasNoNotifyMeTag) {
    return soldOutMessage
  }

  return temporaryOutOfStockMessage
}

const calculateCartTotalExcluding = (cart, products = []) => {
  if (!cart.items.length) return cart.total_price

  const gwpProductPrice = cart.items.find((itemCart) => itemCart.properties?._isGwpSpendThreshold)?.line_price || 0

  if (!products.length) return cart.total_price - gwpProductPrice

  const excludedProductsInCart = cart.items.filter((cartItem) =>
    products.some((product) => product.id === cartItem.product_id)
  )

  if (!excludedProductsInCart.length) return cart.total_price - gwpProductPrice

  const excludedSubtotal = excludedProductsInCart.reduce((total, product) => total + product.line_price, 0)
  const recalculatedTotal = cart.total_price - excludedSubtotal - gwpProductPrice

  return recalculatedTotal < 0 ? cart.total_price : recalculatedTotal
}

const redirectToMarket = (marketUrl) => {
  if (!marketUrl) return

  const localeRoot = window.Shopify.routes.root || '/'

  let pathName = window.location.pathname

  if (localeRoot !== '/' && pathName.startsWith(localeRoot)) {
    pathName = pathName.replace(localeRoot, '/')
  }

  const pagePath = `${pathName}${window.location.search}${window.location.hash}`

  const destinationUrl = `${marketUrl}${pagePath}`

  window.location.href = destinationUrl
}

const fetchProductByAjax = (handles) => {
  const allFetchs = handles.map((handle) =>
    fetch(`${window.Shopify.routes.root}products/${handle}?view=ajax`)
      .then((response) => {
        if (response.status >= 200 && response.status <= 299) {
          return response.json()
        }

        throw Error(response.statusText)
      })
      .then((product) => product)
  )

  return Promise.allSettled(allFetchs).then((results) =>
    results.filter((result) => result.status === 'fulfilled').map((item) => item.value)
  )
}

export {
  add,
  makeArray,
  curry,
  first,
  getProp,
  inArray,
  last,
  map,
  nth,
  partial,
  pipe,
  range,
  setProp,
  whileDo,
  set,
  unset,
  hasOwnProperties,
  inViewPort,
  isEmpty,
  formatName,
  delay,
  handleize,
  decodeHtml,
  formatDate,
  decode,
  numberToWords,
  mapWithOptions,
  fetchProductsRecentlyViewed,
  getOutOfStockMessage,
  calculateCartTotalExcluding,
  redirectToMarket,
  fetchProductByAjax
}
